import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { placeTimeLabel } from "@/lib/datetime";
import BookingStatusCell from "./BookingStatusCell";
import BookingRowActions from "./BookingRowActions";

const fmt = (iso, tz, opts, fn) => new Date(iso)[fn]("en-AU", { ...opts, timeZone: tz || "Australia/Sydney" });
const formatDate = (iso, tz) => (iso ? fmt(iso, tz, { weekday: "short", day: "2-digit", month: "short" }, "toLocaleDateString") : "—");
const formatTime = (iso, tz) => (iso ? fmt(iso, tz, { hour: "2-digit", minute: "2-digit", hour12: false }, "toLocaleTimeString") : "");
const stop = (e) => e.stopPropagation();

const HEADERS = ["Reference", "When", "Site / bay", "Dir", "Status", "Vehicle", "Sender → Receiver"];

const WhenCell = ({ row: r }) => {
  const tz = r.site?.timezone;
  return (
    <TableCell className="whitespace-nowrap">
      <div className="text-sm">{formatDate(r.scheduled_start, tz)}</div>
      <div className="text-xs text-slate-500 tabular-nums">{formatTime(r.scheduled_start, tz)} – {formatTime(r.scheduled_end, tz)}</div>
      {tz && <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`booking-place-time-${r.id}`}>@ {placeTimeLabel(r.site, r.scheduled_start)}</div>}
    </TableCell>
  );
};

const BookingRow = ({ row: r, selected, onToggle, lifecycle, actions }) => (
  <TableRow data-testid={`booking-row-${r.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => actions.edit(r)}>
    <TableCell className="w-8" onClick={stop}>
      <Checkbox checked={selected} onCheckedChange={() => onToggle(r.id)}
                data-testid={`bookings-select-${r.id}`} aria-label={`Select ${r.reference_number}`} />
    </TableCell>
    <TableCell className="font-mono text-xs whitespace-nowrap cursor-pointer">
      <div className="flex items-center gap-1.5">
        {r.reference_number}
        {r.driver_tba && (
          <span className="inline-flex items-center rounded-full bg-amber-100 border border-amber-300 text-amber-800 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
                data-testid={`bookings-tba-chip-${r.id}`} title="Driver TBA — needs assignment">
            Driver TBA
          </span>
        )}
      </div>
    </TableCell>
    <WhenCell row={r} />
    <TableCell className="whitespace-nowrap">
      <div className="flex items-center gap-2">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: r.bay?.color || "#94a3b8" }} />
        <span className="text-sm">{r.site?.name}</span>
      </div>
      <div className="text-xs text-slate-500">{r.bay?.name}</div>
    </TableCell>
    <TableCell><Badge variant="outline" className="capitalize">{r.direction === "inbound" ? "In" : "Out"}</Badge></TableCell>
    <TableCell><BookingStatusCell row={r} lifecycle={lifecycle} /></TableCell>
    <TableCell className="text-sm">{r.vehicle_type || "—"}</TableCell>
    <TableCell className="text-sm">
      {(r.sender_name || "—")} <span className="text-slate-300">→</span>{" "}{(r.receiver_name || "—")}
    </TableCell>
    <TableCell className="text-right" onClick={stop}><BookingRowActions row={r} actions={actions} /></TableCell>
  </TableRow>
);

export default function BookingsTable({ rows, selection, lifecycle, actions }) {
  const { selectedIds } = selection;
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8">
              <Checkbox checked={selectedIds.size > 0 && selectedIds.size === rows.length}
                        onCheckedChange={selection.toggleAll} data-testid="bookings-select-all" aria-label="Select all" />
            </TableHead>
            {HEADERS.map((h) => <TableHead key={h}>{h}</TableHead>)}
            <TableHead className="w-12 text-right">&nbsp;</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <BookingRow key={r.id} row={r} selected={selectedIds.has(r.id)} onToggle={selection.toggleOne}
                        lifecycle={lifecycle} actions={actions} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
